/*
* SCRUM-3980: возвратное состояние заказа («вернуть в аптеку») для экранов пациента.
*
* Источник — свойство заказа `back_to_pharmacy` из /order/{id}/info ({ value, label, changed_at, changed_by } | null).
* `orders.sub_status` на бэке — легаси: `sub_status_slug` читаем только как fallback,
* когда api ещё не отдаёт поле (старая версия api) или отдаёт null по определению (см. FINISHED_STATUS_SLUGS).
*/

export const BACK_TO_PHARMACY_PENDING = 'pending'
export const BACK_TO_PHARMACY_CONFIRMED = 'confirmed'
export const BACK_TO_PHARMACY_IN_TRANSIT = 'in_transit'

// Статус `Return in Transit` — заказ физически едет в аптеку.
const RETURN_IN_TRANSIT_STATUS_SLUG = 'return-in-transit'

// У доехавших заказов api отдаёт back_to_pharmacy = null, а экраны этих статусов
// исторически смотрят на легаси sub_status (он у старых заказов остаётся возвратным).
const FINISHED_STATUS_SLUGS = ['delivered', 'back-to-pharmacy']

// confirmed_asap (возврат сегодня) для пациента ничем не отличается от confirmed.
const PROPERTY_VALUE_TO_STATE = {
  pending: BACK_TO_PHARMACY_PENDING,
  confirmed: BACK_TO_PHARMACY_CONFIRMED,
  confirmed_asap: BACK_TO_PHARMACY_CONFIRMED,
}

// Fallback: легаси саб-статусы возвратного пайплайна.
const SUB_STATUS_SLUG_TO_STATE = {
  'pending-back-to-pharmacy': BACK_TO_PHARMACY_PENDING,
  'confirmed-back-to-pharmacy': BACK_TO_PHARMACY_CONFIRMED,
  'back-to-ph-asap': BACK_TO_PHARMACY_CONFIRMED,
  'on-its-way-to-the-pharmacy': BACK_TO_PHARMACY_IN_TRANSIT,
}

/**
 * @param {object|null|undefined} orderData заказ из /order/{id}/info
 * @returns {'pending'|'confirmed'|'in_transit'|null}
 */
export function getBackToPharmacyState(orderData) {
  if (!orderData) {
    return null
  }

  // «Едет в аптеку»: статус Return in Transit; саб-статус «On its way to the pharmacy» — fallback
  // для заказов старой системы (так же, как api OrderPropertyService::isInTransitToPharmacy).
  // Свойство такого заказа — confirmed, поэтому проверка идёт до него.
  if (orderData.status_slug === RETURN_IN_TRANSIT_STATUS_SLUG
    || SUB_STATUS_SLUG_TO_STATE[orderData.sub_status_slug] === BACK_TO_PHARMACY_IN_TRANSIT) {
    return BACK_TO_PHARMACY_IN_TRANSIT
  }

  const hasPropertyField = Object.prototype.hasOwnProperty.call(orderData, 'back_to_pharmacy')

  if (hasPropertyField && !FINISHED_STATUS_SLUGS.includes(orderData.status_slug)) {
    return PROPERTY_VALUE_TO_STATE[orderData.back_to_pharmacy?.value] || null
  }

  return SUB_STATUS_SLUG_TO_STATE[orderData.sub_status_slug] || null
}

/**
 * Значение свойства для опроса /order-updated (сравнивается на бэке, если ключ прислан).
 * undefined — api без поля: ключ в запрос не попадёт, бэк сравнит по sub_status.
 */
export function getBackToPharmacyValue(orderData) {
  if (!orderData || !Object.prototype.hasOwnProperty.call(orderData, 'back_to_pharmacy')) {
    return undefined
  }

  return orderData.back_to_pharmacy?.value ?? null
}
